import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Check, Copy } from 'lucide-react';
import type { NewTokenResponse } from '@grove/api-client';
import { api } from '../lib/api';
import {
  MCP_DOCS_URL,
  MCP_HOST_PATHS,
  claudeCodeCommand,
  mcpConfigJson,
} from '../lib/mcp';
import { Button } from './Button';
import { Modal } from './Modal';

interface ConnectAgentDialogProps {
  open: boolean;
  onClose: () => void;
}

function CopyBlock({ caption, text }: { caption: string; text: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-0">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border-subtle">
        <span className="text-xs text-text-muted">{caption}</span>
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-1 text-xs font-medium text-violet-400 hover:text-violet-300 transition-colors"
        >
          {copied ? (
            <>
              <Check size={12} /> Copied
            </>
          ) : (
            <>
              <Copy size={12} /> Copy
            </>
          )}
        </button>
      </div>
      {/* The token is 73 chars and only ever shown here — wrap rather than
          clip it, so what's on screen is the whole thing. */}
      <pre className="px-3 py-2.5 text-xs whitespace-pre-wrap break-all text-text-primary">{text}</pre>
    </div>
  );
}

/**
 * The one place a user hands an agent the keys to their queue.
 *
 * Minting the token and showing the MCP config are the same step on purpose:
 * the raw token exists exactly once, in this dialog, and it's useless to the
 * user without the config block it's already pasted into.
 */
export function ConnectAgentDialog({ open, onClose }: ConnectAgentDialogProps) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [created, setCreated] = useState<NewTokenResponse | null>(null);

  const createToken = useMutation<NewTokenResponse>({
    mutationFn: () => api.createToken({ label: label.trim() }),
    onSuccess: (data) => {
      setCreated(data);
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
    },
  });

  function handleClose() {
    setLabel('');
    setCreated(null);
    createToken.reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size={created ? 'lg' : 'md'}
      title={created ? `Connect ${created.label}` : 'Connect an agent'}
    >
      {created ? (
        <div className="space-y-4">
          <div className="rounded-lg p-3 border border-prism-amber/30 bg-prism-amber/10">
            <p className="text-xs inline-flex items-center gap-1.5 text-prism-amber">
              <AlertTriangle size={14} /> This config contains a token that won't be shown again.
            </p>
          </div>

          <CopyBlock caption="MCP server config" text={mcpConfigJson(created.token)} />

          <div className="text-xs text-text-secondary">
            Paste it into your host's config file:
            <ul className="mt-1.5 space-y-1">
              {MCP_HOST_PATHS.map((h) => (
                <li key={h.host} className="flex items-center gap-2">
                  <span className="text-text-muted">{h.host}</span>
                  <code className="font-mono text-xs text-text-primary">{h.path}</code>
                </li>
              ))}
            </ul>
          </div>

          <CopyBlock caption="Or, for Claude Code" text={claudeCodeCommand(created.token)} />

          <div className="flex items-center justify-between gap-4">
            <a
              href={MCP_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-violet-400 hover:text-violet-300 transition-colors"
            >
              Setup guide <ArrowRight size={14} />
            </a>
            <Button onClick={handleClose}>Done</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Name the agent you're connecting. Grove mints it a token and hands you the config to
            paste — one token per agent, so you can revoke just that one later.
          </p>
          <div>
            <label className="block text-sm mb-1.5 text-text-primary">Agent</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Claude Desktop"
              className="w-full px-3 py-2 rounded-lg text-sm text-text-primary bg-surface-0 border border-border-subtle focus:border-prism-violet focus:outline-none"
            />
          </div>
          {createToken.isError && (
            <p className="text-xs text-red-400">Couldn't create the token. Try again.</p>
          )}
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="flex-1"
              disabled={!label.trim() || createToken.isPending}
              onClick={() => createToken.mutate()}
            >
              {createToken.isPending ? 'Creating…' : 'Create config'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
